import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ExclusiveBadge from "@/components/ExclusiveBadge";
import { Button } from "@/components/ui/button";
import useAuth from "@/hooks/useAuth";
import useSocialProviders from "@/hooks/useSocialProviders";
import { useTracking } from "@/components/ClientAnalytics";

export const Route = createFileRoute("/_unauthenticated/auth/link")({
  component: RouteComponent,
  validateSearch: ({
    token,
    deviceType,
    email,
    paywall,
  }: {
    token?: string;
    deviceType?: string;
    email?: string;
    paywall?: "default";
  }) => {
    if (token && deviceType && email) {
      return { token, deviceType, email, paywall };
    }
    return { token: undefined, deviceType: undefined, email: undefined };
  },
});

function RouteComponent() {
  const { t } = useTranslation();
  const { token, deviceType, email, paywall } = Route.useSearch();

  const navigate = useNavigate();

  const { linkEmailProvider } = useSocialProviders();
  const { trackUserLogin } = useTracking();
  const { fillUser } = useAuth();

  const [error, setError] = useState<string | null>(null);

  const handleSendEmailLink = async () => {
    setError(null);

    if (!token || !deviceType || !email) return setError("Invalid link");

    try {
      const auth = await linkEmailProvider(email, token);

      // The home renders with this user's variants, hence the wait.
      const variantsApplied = fillUser(auth);
      trackUserLogin(auth.user.email ?? "", auth.user.trackingId);
      await variantsApplied;

      navigate({ to: "/", search: { paywall } });
    } catch (err) {
      console.error(err);
      if (err instanceof Error) {
        if (err.message.includes("jwt expired")) {
          setError("The link has expired. Please try again.");
        }
      }
      setError("Something went wrong. Please try again.");
    }
  };

  useEffect(() => {
    handleSendEmailLink();
  }, []);

  if (error) {
    return (
      <div className="mx-auto flex w-full max-w-md flex-col items-center justify-center gap-10 self-end px-4 py-8 shadow-lg sm:self-center">
        <div className="flex flex-col items-center gap-4">
          <img
            className="rounded-xl"
            src="/SugarBaby.svg"
            alt="Luni"
            width={100}
            height={100}
          />
          <h1 className="text-center text-3xl font-bold text-white">
            {t("error_auth_link_title")}
          </h1>
        </div>
        <p className="text-md text-center text-balance text-red-500">{error}</p>
        <Button
          type="button"
          className="w-full"
          onClick={() => navigate({ to: "/" })}
        >
          {t("error_auth_link_back_button")}
        </Button>
      </div>
    );
  }

  return (
    <div className="absolute top-0 left-0 flex h-full w-full items-center justify-center">
      <ExclusiveBadge className="size-24 animate-pulse text-neutral-50" />
    </div>
  );
}
